import { redirect, type LoaderFunctionArgs } from "react-router-dom";
import { authControllerMeV1Options } from "@/client/@tanstack/react-query.gen";
import { getTokensInfo } from "@/lib/auth/auth-tokens";
import { ROLES, type RoleSlug } from "@/lib/auth/roles";
import { route, RouteKeys } from "@/services/routing/routes";
import queryClient from "@/services/react-query/query-client";

export type ProtectedLoaderOptions = {
  requiredRoles?: RoleSlug[];
  redirectTo?: string;
};

/**
 * Get the default dashboard path for a user's role
 */
export function getDefaultPathForRole(roleSlug: RoleSlug): string {
  switch (roleSlug) {
    case ROLES.ADMIN:
      return route(RouteKeys.ADMIN_DASHBOARD);
    case ROLES.COMPANY_ADMIN:
    case ROLES.COMPANY_MANAGER:
    case ROLES.USER:
    case ROLES.DROPSHIPPER:
      return route(RouteKeys.DASHBOARD);
    case ROLES.COMPANY_SCANNER:
      return route(RouteKeys.SCANNER);
    default:
      return route(RouteKeys.HOME);
  }
}

export function protectedLoader(options: ProtectedLoaderOptions = {}) {
  const { requiredRoles = [], redirectTo = route(RouteKeys.SIGN_IN) } = options;

  return async (args: LoaderFunctionArgs) => {
    try {
      // Always revalidate auth for protected routes to avoid stale /me cache
      // allowing navigation with expired tokens after external OAuth redirects.
      // In-flight deduplication still prevents duplicate parallel requests.
      const user = await queryClient.fetchQuery({
        ...authControllerMeV1Options(),
        staleTime: 0,
      });

      // Not authenticated
      if (!user) {
        const returnTo = new URL(args.request.url).pathname;
        const search = new URL(args.request.url).search;
        const fullReturnTo = returnTo + search;
        return redirect(`${redirectTo}?returnTo=${encodeURIComponent(fullReturnTo)}`);
      }

      // Check role authorization if required
      if (requiredRoles.length > 0) {
        const userRole = user.role?.slug as RoleSlug;
        if (!requiredRoles.includes(userRole)) {
          // Authenticated but not authorized -- redirect to role-appropriate dashboard
          return redirect(getDefaultPathForRole(userRole));
        }
      }

      return { user };
    } catch (error) {
      const status = (error as { status?: number }).status;
      // Only redirect to sign-in for definitive auth failures:
      // - 401/403 from the server (invalid or missing token)
      // - No access token at all (not yet authenticated)
      // Network errors (no status) fall through to cached data to avoid
      // logging users out due to temporary connectivity issues.
      const isAuthFailure = status === 401 || status === 403 || !getTokensInfo()?.token;

      if (isAuthFailure) {
        const returnTo = new URL(args.request.url).pathname;
        const search = new URL(args.request.url).search;
        const fullReturnTo = returnTo + search;
        return redirect(`${redirectTo}?returnTo=${encodeURIComponent(fullReturnTo)}`);
      }

      // Network or server error — serve stale cached user data if available
      // so the user stays on the page rather than being bounced to sign-in.
      const cachedUser = queryClient.getQueryData(authControllerMeV1Options().queryKey);
      if (cachedUser) return { user: cachedUser };

      // No cache and no tokens — redirect to sign-in as a safe fallback
      const returnTo = new URL(args.request.url).pathname;
      const search = new URL(args.request.url).search;
      const fullReturnTo = returnTo + search;
      return redirect(`${redirectTo}?returnTo=${encodeURIComponent(fullReturnTo)}`);
    }
  };
}
